import EmailFormInput from "./EmailFormInput";
import FormFooter from "./FormFooter";
import FormHeader from "./FormHeader";
import PasswordFormInput from "./PasswordFormInput";
import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import validateEmail from "@/functions/ValidateEmail";
import validatePassword from "@/functions/ValidatePassword";
import nProgress from "nprogress";
import { signIn } from "next-auth/react";
import Divider from "./Divider";
import SubmitButton from "./SubmitButton";
import SecondaryActions from "./SecondaryActions";
import encryptData from "@/functions/encryptInput";
import { useLoginService } from "@/services/AuthenticationService";
import GoogleAuthButton from "./GoogleAuthButton";

type LoginFormProps = {
    errorMessage?: string
}

interface LoginInput {
    email: string
    password: string
    rememberMe: boolean
}

interface LoginValidation {
    email: string,
    password: string
}

export default function LoginForm(props: LoginFormProps) {

    const { errorMessage } = props;

    const [loginInput, setLoginInput] = useState<LoginInput>({
        email: "",
        password: "",
        rememberMe: false
    })

    const [validation, setValidation] = useState<LoginValidation>({
        email: "",
        password: ""
    })

    const { loginHandler } = useLoginService();

    useEffect(() => {
        if (errorMessage) {
            setValidation({...validation, password: errorMessage})
        }
    }, [errorMessage, validation])

    const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
        const { id, value, type, checked } = e.currentTarget;
    
        if (type === "checkbox") {
            setLoginInput({ ...loginInput, [id]: checked });
        } else {
            setLoginInput({ ...loginInput, [id]: value });
        }
    }

    const isFormEmpty = () => {
        const { email, password } = loginInput;
        const isEmailInputValid = isEmailValid(email);
        const isPasswordInputValid = isPasswordValid(password);
       
        return isEmailInputValid && isPasswordInputValid;
    }

    const isInputValid = (componentId: string, isValid: boolean, validationMessage: string) => {
        const message = isValid ? "" : validationMessage;
        setValidation((v) => v = {...v, [componentId]: message});
        return isValid;
    }

    const isEmailValid = (email: string) => {
        const { isValid, validationMessage } = validateEmail(email); 
        return isInputValid("email", isValid, validationMessage);
    }

    const isPasswordValid = (password: string) => {
        const { isValid, validationMessage } = validatePassword(password, false, false); 
        return isInputValid("password", isValid, validationMessage);
    }

    const handleLogin = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!isFormEmpty()) return;

        await login();
    }

    const login = async () => {
        try {
            const encryptedEmail = encryptData(loginInput.email);
            const encryptedPassword = encryptData(loginInput.password);

            const response = await loginHandler(encryptedEmail, encryptedPassword, loginInput.rememberMe);
            
            if (response.data?.success) {
                nProgress.start();
                await signIn("oidc", {
                    callbackUrl: "/",
                    redirect: false
                });
            } else {
                setValidation({
                    ...validation, 
                    password: response.data?.errorMessage || "Account not found"
                });
            }
        } catch (error) {
            setValidation({
                ...validation,
                password: "Failed to process login. Please try again."
            });
            console.error("Login error:", error);
        } finally {
            nProgress.done();
        }
    }

    return (
        <div className="flex justify-center items-center min-h-screen m-2">
            <div className="bg-white p-12 rounded-xl mx-auto my-20 lg:mx-20">
                <form onSubmit={handleLogin} noValidate>

                    <FormHeader headerText="Welcome to Accelist Career" />
                    
                    <EmailFormInput 
                        id="email"
                        value={loginInput.email}
                        onChange={handleInput}
                        validationMessage={validation.email}
                        autoFocus={true}
                    />

                    <PasswordFormInput 
                        id="password"
                        value={loginInput.password}
                        onChange={handleInput}
                        validationMessage={validation.password}
                    />

                    <SecondaryActions 
                        rememberMe={loginInput.rememberMe} 
                        onCheckBoxChange={handleInput}
                    />

                    <SubmitButton buttonText="Login" />

                    <FormFooter
                        footerText="Don't have an account?"
                        actionText="Create an Account"
                        actionHref="/register"
                    />

                    <Divider />

                    <GoogleAuthButton text={"Sign in with Google"}/>
                    
                </form>
            </div>
        </div>
    );
}